import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, LogIn, Loader2 } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (!user || !user.role) {
        toast.error("Réponse inattendue du serveur. Réessayez.");
        return;
      }
      toast.success("Bienvenue");
      // Dual-role : si l'user a basculé en mode conducteur depuis son
      // dashboard passager, on respecte son `active_mode` sauvegardé
      // au lieu de retomber sur le role primaire d'inscription.
      const mode = user.active_mode || user.role;
      if (user.role === "hotel") nav("/dashboard/hotel");
      else if (mode === "driver") nav("/dashboard/driver");
      else if (mode === "client") nav("/dashboard/client");
      else if (user.role === "admin") nav("/admin");
      else nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = formatApiError(detail);
      if (!detail) {
        if (err.response?.status) {
          msg = `Erreur serveur (HTTP ${err.response.status}). Réessayez dans un instant.`;
        } else if (err.message && !err.message.includes("Network")) {
          msg = err.message;
        } else {
          msg = "Impossible de joindre le serveur. Vérifiez votre connexion internet.";
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="login-page"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.42), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(354, 88%, 66%, 0.30), transparent 70%)" }}
      />

      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <Link
          to="/"
          className="street-press mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-foreground street-border-thin street-shadow-sm"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Retour
        </Link>

        <header className="mt-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary rotate-[-4deg] street-border street-shadow-sm">
            <LogIn className="h-8 w-8 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <h1 className="mt-5 font-[Outfit] text-[38px] font-black leading-[1.02] tracking-tight text-foreground">
            Salut,
            <span className="ml-1 inline-block rounded-xl bg-accent px-2 py-0.5 street-border-thin rotate-[-2deg] text-accent-foreground">
              connecte-toi
            </span>
          </h1>
          <p className="mt-3 text-sm text-foreground/70 font-medium">
            Content de te revoir sur Price !
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alex@email.com"
            autoComplete="email"
            testid="login-email"
          />
          <Field
            label="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Ton mot de passe"
            autoComplete="current-password"
            testid="login-password"
          />

          <button
            type="submit"
            disabled={loading}
            className="street-press mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Se connecter
          </button>
        </form>

        <div className="mt-8 rounded-3xl bg-white p-5 street-border-thin street-shadow-sm">
          <p className="text-[13px] font-bold uppercase tracking-wide text-foreground/60">Nouveau sur Price ?</p>
          <div className="mt-3 flex flex-col gap-2">
            <Link
              to="/register/client"
              className="street-press inline-flex items-center justify-between rounded-2xl bg-primary/10 px-4 py-3 font-[Outfit] text-[14px] font-bold text-foreground street-border-thin"
              data-testid="login-to-client"
            >
              <span>👥 Créer un compte covoiturage</span>
              <span className="text-primary">→</span>
            </Link>
            <Link
              to="/register/driver"
              className="street-press inline-flex items-center justify-between rounded-2xl bg-accent/20 px-4 py-3 font-[Outfit] text-[14px] font-bold text-foreground street-border-thin"
              data-testid="login-to-driver"
            >
              <span>🚗 Devenir covoitureur</span>
              <span className="text-primary">→</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", autoComplete, testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-bold uppercase tracking-wide text-foreground/80">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
        data-testid={testid}
        className="w-full rounded-2xl bg-white px-4 py-3.5 font-[Outfit] text-[15px] font-medium text-foreground placeholder:text-foreground/35 street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25"
      />
    </label>
  );
}
